import { fetchWithHandling } from '../utils/common-utils';

const BASE_URL = `${process.env.REACT_APP_BACKEND_API_PREFIX}/api/v1/weekly-schedule`;

// ── NOTES ──────────────────────────────────────────────────────────────────────

export interface WeeklyNote {
  id: number;
  weekStartDate: string;
  dayOfWeek: number;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export const getNotesByWeek = async (
  token: string,
  onSessionExpired: () => void,
  weekStartDate: string
): Promise<WeeklyNote[] | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/notes?weekStartDate=${weekStartDate}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );
    return (jsonData as WeeklyNote[] | null) || [];
  } catch (error) {
    return undefined;
  }
};

export const createOrUpdateNote = async (
  token: string,
  onSessionExpired: () => void,
  weekStartDate: string,
  dayOfWeek: number,
  notes: string
): Promise<WeeklyNote | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/notes`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ weekStartDate, dayOfWeek, notes }),
      },
      onSessionExpired
    );
    return jsonData as WeeklyNote | null || undefined;
  } catch (error) {
    return undefined;
  }
};

export const deleteNote = async (
  token: string,
  onSessionExpired: () => void,
  dayOfWeek: number,
  weekStartDate: string
): Promise<boolean> => {
  try {
    const res = await fetchWithHandling(
      `${BASE_URL}/notes/${dayOfWeek}?weekStartDate=${weekStartDate}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );
    return res !== null && res !== undefined;
  } catch (error) {
    return false;
  }
};

// ── ON-CALL / REPERIBILI ───────────────────────────────────────────────────────

export interface OnCallEmployee {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface OnCall {
  id: number;
  weekStartDate: string;
  dayOfWeek: number;
  employee: OnCallEmployee;
  createdAt: string;
  updatedAt: string;
}

export const getOnCallByWeek = async (
  token: string,
  onSessionExpired: () => void,
  weekStartDate: string
): Promise<OnCall[] | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/on-call?weekStartDate=${weekStartDate}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );
    return (jsonData as OnCall[] | null) || [];
  } catch (error) {
    return undefined;
  }
};

export const addOnCall = async (
  token: string,
  onSessionExpired: () => void,
  weekStartDate: string,
  dayOfWeek: number,
  employeeId: number
): Promise<OnCall | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/on-call`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ weekStartDate, dayOfWeek, employeeId }),
      },
      onSessionExpired
    );
    return jsonData as OnCall | null || undefined;
  } catch (error) {
    return undefined;
  }
};

export const removeOnCall = async (
  token: string,
  onSessionExpired: () => void,
  dayOfWeek: number,
  employeeId: number,
  weekStartDate: string
): Promise<boolean> => {
  try {
    const res = await fetchWithHandling(
      `${BASE_URL}/on-call/${dayOfWeek}/${employeeId}?weekStartDate=${weekStartDate}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );
    return res !== null && res !== undefined;
  } catch (error) {
    return false;
  }
};

// ── FERIE ────────────────────────────────────────────────────────────────────

export interface FerieEmployee {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface Ferie {
  id: number;
  weekStartDate: string;
  dayOfWeek: number;
  employee: FerieEmployee;
  createdAt: string;
  updatedAt: string;
}

export const getFerieByWeek = async (
  token: string,
  onSessionExpired: () => void,
  weekStartDate: string
): Promise<Ferie[] | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/ferie?weekStartDate=${weekStartDate}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );
    return (jsonData as Ferie[] | null) || [];
  } catch (error) {
    return undefined;
  }
};

export const addFerie = async (
  token: string,
  onSessionExpired: () => void,
  weekStartDate: string,
  dayOfWeek: number,
  employeeId: number
): Promise<Ferie | undefined> => {
  try {
    const jsonData = await fetchWithHandling(
      `${BASE_URL}/ferie`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ weekStartDate, dayOfWeek, employeeId }),
      },
      onSessionExpired
    );
    return jsonData as Ferie | null || undefined;
  } catch (error) {
    return undefined;
  }
};

export const removeFerie = async (
  token: string,
  onSessionExpired: () => void,
  dayOfWeek: number,
  employeeId: number,
  weekStartDate: string
): Promise<boolean> => {
  try {
    const res = await fetchWithHandling(
      `${BASE_URL}/ferie/${dayOfWeek}/${employeeId}?weekStartDate=${weekStartDate}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      onSessionExpired
    );
    return res !== null && res !== undefined;
  } catch (error) {
    return false;
  }
};
